/**
 * The HTTP client.
 *
 * Two things it exists to get right:
 *
 * **Token storage.** The access token lives in a module-level variable — in
 * memory only. It is never written to `localStorage`, because anything there
 * is readable by any script that gets injected into the page. The refresh
 * token is an HttpOnly cookie the browser sends on its own; this file cannot
 * read it, which is the point.
 *
 * **Transparent refresh.** A 401 triggers one refresh attempt and one replay
 * of the original request. Concurrent 401s share a single refresh promise, so
 * a page that fires six requests on load does not fire six refreshes and
 * rotate the token out from under itself.
 */

const BASE = import.meta.env.VITE_API_URL ?? '/api'

let accessToken = null
let refreshPromise = null
/** Called when refresh fails for good, so the auth store can sign out. */
let onAuthFailure = null

export function setAccessToken(token) {
  accessToken = token
}

export function getAccessToken() {
  return accessToken
}

export function setAuthFailureHandler(handler) {
  onAuthFailure = handler
}

/** Thrown for every non-2xx response, carrying the API's error envelope. */
export class ApiError extends Error {
  constructor(status, body, requestId) {
    const error = body?.error ?? {}
    super(error.message || 'Something went wrong')
    this.name = 'ApiError'
    this.status = status
    this.code = error.code ?? 'UNKNOWN'
    /** Field-level problems, keyed by dotted path — feeds form error slots. */
    this.details = error.details ?? null
    this.requestId = error.requestId ?? requestId
    this.body = body
  }

  /** First message for a given field, for inline form errors. */
  fieldError(field) {
    return this.details?.[field]?.[0] ?? null
  }

  get isNetwork() {
    return this.status === 0
  }

  get isValidation() {
    return this.status === 422
  }

  get isAuth() {
    return this.status === 401
  }

  get isForbidden() {
    return this.status === 403
  }
}

async function parseResponse(response) {
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return { raw: text }
  }
}

/**
 * Refreshes the access token, collapsing concurrent callers onto one request.
 */
async function refreshAccessToken() {
  refreshPromise ??= (async () => {
    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      })

      if (!response.ok) throw new ApiError(response.status, await parseResponse(response))

      const data = await response.json()
      accessToken = data.accessToken
      return data
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

/**
 * @param {string} method
 * @param {string} path
 * @param {{body?: unknown, query?: object, headers?: object, signal?: AbortSignal, skipAuthRetry?: boolean}} [options]
 */
async function request(method, path, options = {}) {
  const { body, query, headers = {}, signal, skipAuthRetry = false } = options

  let url = `${BASE}${path}`
  if (query) {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue
      if (Array.isArray(value)) {
        if (value.length) params.set(key, value.join(','))
      } else {
        params.set(key, String(value))
      }
    }
    const queryString = params.toString()
    if (queryString) url += `?${queryString}`
  }

  const send = async () => {
    let response
    try {
      response = await fetch(url, {
        method,
        // Sends and receives the refresh cookie.
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          ...headers,
        },
        body: body === undefined ? undefined : JSON.stringify(body),
        signal,
      })
    } catch (error) {
      if (error.name === 'AbortError') throw error
      // A failed fetch has no status; give it one the UI can branch on.
      throw new ApiError(0, {
        error: { code: 'NETWORK_ERROR', message: 'Could not reach the server. Check your connection.' },
      })
    }

    const data = await parseResponse(response)
    if (!response.ok) {
      throw new ApiError(response.status, data, response.headers.get('x-request-id'))
    }
    return data
  }

  try {
    return await send()
  } catch (error) {
    const canRetry =
      error instanceof ApiError &&
      error.status === 401 &&
      !skipAuthRetry &&
      // Refreshing in response to a failed refresh or login would loop.
      !path.startsWith('/auth/refresh') &&
      !path.startsWith('/auth/login')

    if (!canRetry) throw error

    try {
      await refreshAccessToken()
    } catch {
      accessToken = null
      onAuthFailure?.()
      throw error
    }

    return send()
  }
}

export const api = {
  get: (path, query, options) => request('GET', path, { ...options, query }),
  post: (path, body, options) => request('POST', path, { ...options, body }),
  patch: (path, body, options) => request('PATCH', path, { ...options, body }),
  put: (path, body, options) => request('PUT', path, { ...options, body }),
  delete: (path, options) => request('DELETE', path, options),
  refresh: refreshAccessToken,
}
